import { Component, Input, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NgControl, UntypedFormGroup } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { OrderingOption, PhoneTypes } from 'generated/graphql-types';
import { Observable, of } from 'rxjs';
import { switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { Register } from 'src/app/common-components/register/models/register';
import { DeviceDetailSaveEvent } from 'src/app/pages/device-details/models/device-detail-form-submit-event';
import * as DeviceDetailActions from 'src/app/pages/device-details/state/device-detail.actions';
import { DevicePrivileges } from 'src/app/pages/device-register/models/device-privileges';
import { DeviceRegisterListItem } from 'src/app/pages/device-register/models/device-register-results.model';
import { rebootPhone, resetPhoneZtp } from 'src/app/pages/device-register/state/device-register.actions';
import { ExtensionHackedFraudDetails } from 'src/app/pages/extension-details/helpers/hacked-fraud-helper';
import { getHackedFraudDetails } from 'src/app/pages/extension-details/state/extension-detail.selectors';
import { getDevicePrivileges } from 'src/app/privileges/privileges.selector';
import { getSelectedAccountShortName } from 'src/app/store/select-account/select-account.selector';
import * as AddPhoneActions from '../../../add-phone/state/add-phone.actions';
import { phoneRegisterColumns } from '../../helpers/phones-helper';
import { PhonePrivileges } from '../../models/phone-privileges';
import { PhoneRegisterOptions } from '../../models/phone-register-options';
import { PhoneRegisterListItem } from '../../models/phone-register-results.model';
import { PhoneTypeOption } from '../../models/phone-type-option';
import * as ExtensionPhoneRegisterActions from '../state/extension-phone-register.actions';
import { extensionPhoneRegisterAdapterActions } from '../state/extension-phone-register.actions';
import * as extensionPhoneRegisterReducer from '../state/extension-phone-register.reducer';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-extension-phone-register',
    styleUrls: ['./extension-phone-register.component.scss'],
    templateUrl: './extension-phone-register.component.html',
    standalone: false
})
export class ExtensionPhoneRegisterComponent
   extends Register<PhoneRegisterListItem, PhoneRegisterOptions, OrderingOption>
   implements ControlValueAccessor
{
   @Input() phonePrivileges: PhonePrivileges;

   @Input() accountId: number | null;

   @Input() callState: CallState;

   @Input() form: UntypedFormGroup;

   accountShortName: string | null;

   createPhoneTypeOptions: PhoneTypeOption[];

   hackedFraudDetails: ExtensionHackedFraudDetails | null;

   isSystemUser$: Observable<boolean>;

   entityName = 'Phone';

   columns = [...phoneRegisterColumns, 'sipUser', 'isConnected', 'numberOfLineKeys', 'phoneActionsMenu'];

   onTouched: () => void;

   private onChange: (value: Array<PhoneRegisterListItem> | null) => void;

   devicePrivileges$: Observable<DevicePrivileges>;

   constructor(
      public store: Store,
      private control: NgControl,
      private actions$: Actions,
      private confirmationService: ConfirmationDialogService
   ) {
      super(store, extensionPhoneRegisterReducer.selectors, extensionPhoneRegisterAdapterActions);
      this.isSystemUser$ = this.store.pipe(select(getCurrentUserIsSystemUser));
      this.setPhoneTypeOptions(Object.values(PhoneTypes));
      this.devicePrivileges$ = this.store.pipe(select(getDevicePrivileges));
      control.valueAccessor = this;

      this.store.select(getSelectedAccountShortName).subscribe((shortName) => {
         this.accountShortName = shortName;
      });

      this.store.select(getHackedFraudDetails).subscribe((hackedFraudDetails) => {
         this.hackedFraudDetails = hackedFraudDetails;
      });

      this.actions$
         .pipe(
            ofType(AddPhoneActions.addPhoneToRegister, ExtensionPhoneRegisterActions.deleteExtensionPhone),
            withLatestFrom(this.store.select(extensionPhoneRegisterReducer.selectPhones)),
            takeUntil(this.destroyed$)
         )
         .subscribe(([action, phones]) => {
            const sipUsernames = phones.map((phone) => phone.sipUsers?.map((sipUser) => sipUser.userName));
            this.form.get('extensionPhone.sipUsernames')!.setValue(sipUsernames);
            this.checkForDuplicateSipUsernames(phones);
            this.onChange(phones);
         });
   }

   writeValue(obj: any): void {}

   registerOnChange(onChange: (value: Array<PhoneRegisterListItem> | null) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setPhoneTypeOptions(phoneTypes: string[]): PhoneTypeOption[] {
      this.createPhoneTypeOptions = Object.values(PhoneTypes)
         .filter((type) => {
            return phoneTypes.includes(type) && type !== PhoneTypes.Appsuite;
         })
         .map((type) => {
            return { name: type };
         });

      this.createPhoneTypeOptions.sort((a, b) => {
         return a.name.localeCompare(b.name);
      });

      return this.createPhoneTypeOptions;
   }

   onCreatePhoneTypeSelected(phoneTypeOption: PhoneTypeOption): void {
      this.store.dispatch(
         ExtensionPhoneRegisterActions.createExtensionPhone({
            accountId: this.accountId,
            accountShortName: this.accountShortName,
            createPhoneTypeOption: phoneTypeOption,
         })
      );
   }

   onEdit(phone: PhoneRegisterListItem): void {
      this.store.dispatch(ExtensionPhoneRegisterActions.editExtensionPhone({ accountId: this.accountId, phone }));
   }

   onRebootPhone(deviceId: number) {
      this.store.dispatch(rebootPhone({ deviceId: deviceId }));
   }

   onResetPhoneZtp(deviceId: number) {
      this.store.dispatch(resetPhoneZtp({ deviceId: deviceId }));
   }

   deleteConfirmationDialog(): Observable<boolean> {
      const message = 'Deleting this phone will switch the extension to Ring All Phones.';

      const dialogRef = this.confirmationService.confirm({
         data: {
            header: 'Delete this Phone?',
            message,
         },
         size: DialogSize.Medium,
      });

      return dialogRef.afterClosed().pipe(
         takeUntil(this.destroyed$),
         switchMap((confirmed) => {
            if (confirmed) {
               return of(true);
            } else {
               return of(false);
            }
         })
      );
   }

   onDelete(phoneId: number): void {
      if (phoneId === this.form.get('extensionPhone.autoAnswerPhone')?.value?.phoneId) {
         this.deleteConfirmationDialog()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((confirmed) => {
               if (confirmed) {
                  this.store.dispatch(ExtensionPhoneRegisterActions.deleteExtensionPhone({ phoneId }));
               }
            });
      } else {
         this.store.dispatch(ExtensionPhoneRegisterActions.deleteExtensionPhone({ phoneId }));
      }
   }

   checkForDuplicateSipUsernames(phones: PhoneRegisterListItem[]): void {
      const sipUsernamesMap = new Map<number, string>();
      phones.forEach((phone) => {
         phone.sipUsers?.forEach((sipUser) => {
            if (phone.phoneId && sipUser.userName) sipUsernamesMap.set(phone.phoneId, sipUser.userName);
         });
      });

      const invalidPhoneIds = Array.from(sipUsernamesMap.entries())
         .filter((entry) => {
            return Array.from(sipUsernamesMap.values()).filter((value) => value === entry[1]).length > 1;
         })
         .map((entry) => entry[0]);

      this.store.dispatch(ExtensionPhoneRegisterActions.updateInvalidPhones({ invalidPhoneIds: invalidPhoneIds }));
   }

   onResetHackedAndFraud(sipUserId: number): void {
      this.store.dispatch(ExtensionPhoneRegisterActions.changeSipUserPassword({ sipUserId }));
   }

   onDeviceReleasedToSystem(event: DeviceDetailSaveEvent): void {
      this.store.dispatch(DeviceDetailActions.releaseDeviceDetail(event));
   }

   onReleaseToAccount(device: DeviceRegisterListItem): void {
      if (device.deviceId) {
         this.store.dispatch(DeviceDetailActions.releaseDeviceToAccount({ deviceId: device.deviceId }));
      }
   }

   onDeleteDevice(event: { deviceId: number }) {
      this.store.dispatch(DeviceDetailActions.deleteDevice(event));
   }
}
